import { useEffect, useState } from "react";
import { api, Yo } from "../api/client";

const COLS = "minmax(0,1.5fr) 128px 128px 116px 96px";

const ROLES: Record<string, string> = {
  superadmin: "Administrador total",
  admin: "Administrador",
  miembro: "Analista",
};

export default function UsersPage({ yo }: { yo?: Yo | null }) {
  const [items, setItems] = useState<any[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("miembro");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  // Distinto de `msg`: una falla al CARGAR no puede mostrarse como una lista
  // vacia. "Todavia no hay usuarios" al lado de un error dice dos cosas
  // opuestas, y la tranquilizadora es la falsa.
  const [falla, setFalla] = useState<string | null>(null);
  // Roles que ESTA persona puede asignar. Lo dice el backend, no se deduce
  // acá: un administrador solo gestiona analistas, y ofrecerle un rol que le
  // van a rechazar con 403 es hacerle perder el tiempo.
  const [asignables, setAsignables] = useState<string[]>(["miembro"]);

  async function cargar() {
    try {
      const r = await api.getUsers();
      setItems(r.items ?? []);
      setAsignables(r.puede_gestionar ?? ["miembro"]);
      setMsg("");
      setFalla(null);
    } catch (e: any) {
      setFalla(e.message);
    }
  }
  useEffect(() => { cargar(); }, []);

  async function agregar() {
    if (!email.trim()) return;
    setMsg("Guardando...");
    try {
      await api.upsertUser({ email: email.toLowerCase().trim(), role, name, active: true });
      setEmail(""); setName(""); setRole("miembro");
      await cargar();
    } catch (e: any) {
      setMsg(e.message);
    }
  }

  async function alternar(u: any) {
    try {
      await api.upsertUser({ email: u.email, role: u.role, name: u.name, active: !u.active });
      await cargar();
    } catch (e: any) {
      setMsg(e.message);
    }
  }

  return (
    <>
      <div className="card card-pad" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 10 }}>
          Dar acceso a alguien
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input placeholder="correo@prestamype.com" value={email} style={{ flex: 2, minWidth: 220 }}
                 onChange={(e) => setEmail(e.target.value)} />
          <input placeholder="Nombre (opcional)" value={name} style={{ flex: 1, minWidth: 140 }}
                 onChange={(e) => setName(e.target.value)} />
          <span className="rev-seg">
            {Object.entries(ROLES)
              .filter(([v]) => asignables.includes(v))
              .map(([v, l]) => (
                <button key={v} className={role === v ? "on" : ""}
                        onClick={() => setRole(v)}>
                  {l}
                </button>
              ))}
          </span>
          <button className="btn primary" onClick={agregar} disabled={!email.trim()}>
            Agregar
          </button>
        </div>
        <div style={{ fontSize: 12, color: "var(--faint)", marginTop: 10 }}>
          Con el correo alcanza. El nombre se completa solo la primera vez que
          la persona entra con Google.
          {!yo?.puede?.gestionar_admins && (
            <> Como administrador puedes dar y quitar acceso a analistas; los
            demás administradores los gestiona un administrador total.</>
          )}
        </div>
        {msg && (
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 8 }}>{msg}</div>
        )}
      </div>

      <div className="card">
        <div className="thead" style={{ gridTemplateColumns: COLS }}>
          <div>Persona</div><div>Rol</div><div>Último acceso</div><div>Decisiones 30 d</div><div />
        </div>
        {falla ? (
          <div className="empty falla">
            <div className="falla-titulo">No se pudo cargar la lista de usuarios.</div>
            <div className="falla-detalle mono">{falla}</div>
            <div className="falla-nota">
              Puede haber gente con acceso. Esta pantalla no la está mostrando.
            </div>
            <button className="btn" onClick={cargar}>Reintentar</button>
          </div>
        ) : items.length === 0 ? (
          <div className="empty">Todavía no hay usuarios.</div>
        ) : null}
        {items.map((u) => (
          <div key={u.email}
               className="trow"
               style={{ gridTemplateColumns: COLS, opacity: u.active ? 1 : .55 }}>
            <div className="hist-quien">
              <span className="hist-avatar"
                    style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>
                {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
              </span>
              <span style={{ minWidth: 0 }}>
                <div className="col-titular">{u.name || u.email}</div>
                {u.name && <div className="col-op">{u.email}</div>}
              </span>
            </div>

            <div>
              <span className={"badge " + (u.role === "superadmin" ? "bad"
                : u.role === "admin" ? "warn" : "ok")}>
                {ROLES[u.role] ?? u.role}
              </span>
            </div>

            {/* Un acceso que nunca se usó es el que conviene revisar primero:
                o la persona no lo necesitaba, o nadie le avisó que lo tiene. */}
            <div style={{ fontSize: 12.5,
                          color: u.ultimo_acceso ? "var(--muted)" : "var(--warn)" }}>
              {u.ultimo_acceso
                ? new Date(u.ultimo_acceso * 1000).toLocaleDateString("es-PE",
                    { day: "2-digit", month: "short", year: "numeric" })
                : "Nunca entró"}
            </div>

            <div className="tabular" style={{ fontSize: 12.5, color: "var(--muted)" }}>
              {u.decisiones_30d ?? 0}
            </div>
            <div style={{ textAlign: "right" }}>
              <button className={"btn" + (u.active ? " danger" : "")}
                      onClick={() => alternar(u)}>
                {u.active ? "Quitar" : "Restaurar"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Qué puede hacer cada rol. Estaba solo en la cabeza de quien configuró
          los permisos; ahora está donde se otorgan. */}
      <div className="roles-leyenda">
        {[
          { rol: "Analista", clase: "ok",
            puede: "Ve la cola, el histórico y las métricas. Aprueba y rechaza depósitos." },
          { rol: "Administrador", clase: "warn",
            puede: "Todo lo anterior, más Configuración y Trazabilidad. Da y quita acceso solo a analistas." },
          { rol: "Admin total", clase: "bad",
            puede: "Todo. Es el único que puede gestionar otros administradores y activar el modo real." },
        ].map((r) => (
          <div key={r.rol} className="card card-pad">
            <span className={"badge " + r.clase}>{r.rol}</span>
            <div className="roles-txt">{r.puede}</div>
          </div>
        ))}
      </div>
    </>
  );
}
